import { Token } from '@frontend/authentication-v2';
import { DateFormat, formatDateTimeWithLocale } from '@frontend/common';
import { DeviceSensorClient } from '@frontend/device/api';
import { SensorLevel, SensorType } from '@frontend/device/types';
import { MqttBroker } from '@frontend/pub-sub';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { ZhilaiDriverProps } from './zhilai-driver.component';

interface ViewProps {
    messages: { incoming: boolean; message: string; info: string; slot_id?: string | string[]; timestamp: string; data: any }[];
}

interface ActuatorData {
    account_id: string;
    iot_id: string;
    device_id: string;
    locker_id: string;
    data: { name: string; slot: string; ip_address: string };
    ids: {
        spot_id: string;
        module_id: string;
        slot_id: string;
    };
}

const useZhilaiDriver = (props: ZhilaiDriverProps): ViewProps => {
    const intl = useIntl();
    const subscriberId = 'zhilai-driver-emulator-' + props.iot.id;
    const [messages, setMessages] = useState<{ incoming: boolean; message: string; info: string; slot_id?: string | string[]; timestamp: string; data: any }[]>(
        []
    );

    const onOpen = async (actuator: ActuatorData, token: Token) => {
        console.log('[ZhilaiDriver Emulator] Handling open action with data:', actuator);
        const body = {
            level: SensorLevel.INFO,
            type: SensorType.DOOR_STATUS,
            locker_id: actuator.locker_id,
            data: {
                status: 'opened'
            },
            ids: actuator.ids
        };
        addMessage(false, 'Sent locker opened', 'opened', body, actuator.ids.slot_id);
        return DeviceSensorClient.postDeviceSensor(actuator.account_id, actuator.iot_id, actuator.device_id, body, true, token)
            .then(() => {
                console.log('[ZhilaiDriver Emulator] Sensor data posted successfully');
            })
            .catch((error) => {
                console.error('[ZhilaiDriver Emulator] Failed to post sensor data:', error);
            });
    };

    const onOpenWaitClose = async (actuator: ActuatorData, token: Token) => {
        console.log('[ZhilaiDriver Emulator] Handling open_wait_close action with data:', actuator);
        onOpen(actuator, token);
        console.log('[ZhilaiDriver Emulator] Sensor data posted successfully for open_wait_close');
        // Simulate door closing after 5 seconds
        await wait(5000);
        const body = {
            level: SensorLevel.INFO,
            type: SensorType.DOOR_STATUS,
            locker_id: actuator.locker_id,
            data: {
                status: 'closed'
            },
            ids: actuator.ids
        };
        addMessage(false, 'Sent locker closed', 'closed', body, actuator.ids.slot_id);
        return DeviceSensorClient.postDeviceSensor(actuator.account_id, actuator.iot_id, actuator.device_id, body, true, token)
            .then(() => {
                console.log('[ZhilaiDriver Emulator] Sensor data posted successfully for door closing');
            })
            .catch((error) => {
                console.error('[ZhilaiDriver Emulator] Failed to post sensor data for door closing:', error);
            });
    };

    const callback = (message: string, data: any) => {
        console.log(`[ZhilaiDriver Emulator] Received message: ${message} with data:`, data);

        if (data.type === 'open' && data.actuators && Array.isArray(data.actuators) && data.actuators.length > 0) {
            addMessage(
                true,
                message,
                data.type,
                data,
                data.actuators.map((actuator: ActuatorData) => actuator.ids.slot_id)
            );
            data.actuators.forEach((actuator: ActuatorData) => onOpen(actuator, props.token));
        } else if (data.type === 'open_wait_close' && data.actuators && Array.isArray(data.actuators) && data.actuators.length > 0) {
            addMessage(
                true,
                message,
                data.type,
                data,
                data.actuators.map((actuator: ActuatorData) => actuator.ids.slot_id)
            );
            data.actuators.forEach((actuator: ActuatorData) => onOpenWaitClose(actuator, props.token));
        } else if (data.type === 'door_status' && data.data.status) {
            addMessage(true, message, data.data.status, data, data.ids?.slot_id);
        }
    };

    const addMessage = (incoming: boolean, message: string, info: string, data: any, slot_id?: string | string[]) => {
        const time = formatDateTimeWithLocale(new Date(), intl.locale, DateFormat.HH_MM_SS_MS);
        setMessages((prev) => [{ incoming, message, info, slot_id, timestamp: time, data }, ...prev]);
    };

    useEffect(() => {
        MqttBroker.getInstance().subscribe(subscriberId, `iots/${props.iot.id}/actuator`, callback);
        MqttBroker.getInstance().subscribe(subscriberId, `iots/${props.iot.id}/sensor`, callback);
        return () => {
            MqttBroker.getInstance().unSubscribe(subscriberId, `iots/${props.iot.id}/actuator`);
            MqttBroker.getInstance().unSubscribe(subscriberId, `iots/${props.iot.id}/sensor`);
        };
    }, []);

    return {
        messages
    };
};

export default useZhilaiDriver;

async function wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
